import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {getObjectUrlParams, getStringUrlParams} from '@core/utils/filter-converter';
import {
  ApiConsultationCountByDays,
  Consultation,
  ConsultationCountByDays
} from './interfaces/consultation.interface';
import {
  CreateConsultationFilter,
  CreateConsultationResultFilter,
  GetConsultationByIdFilter,
  GetConsultationsCountByDaysFilter
} from './interfaces/consultations-filter.interface';
import {
  ApiConsultationCreateResponse,
} from './interfaces/consultations-response.interface';
import {ApiResponse} from '@core/interfaces/response.interface';
import {ConsultationsListFilter} from '@consultations/interfaces/consultations-list-filter.interface';
import {prepareConsultation} from '@core/utils/prepare-entity/prepare-consultation';
import {JsonaService} from '@core/services/jsona.service';
import {DeserializedJsonInterface} from '@core/interfaces/jsona.interface';
import {DirectionsInterface} from '@shared-modules/medications/interfaces/directions.interface';

@Injectable()
export class ConsultationsService {
  rescheduleConsultationId$ = new BehaviorSubject<number | null>(null);

  private doctors4uaUrl = `${environment.doctors4uaUrl}/consultations`;
  private doctors4uaUrlV2 = `${environment.doctors4uaUrlV2}/consultations`;

  constructor(
    private http: HttpClient,
    private jsonaService: JsonaService
  ) {
  }

  createConsultation(filter: CreateConsultationFilter): Observable<Consultation> {
    const params = getObjectUrlParams(filter);

    return this.http
      .post<ApiConsultationCreateResponse>(this.doctors4uaUrl, params)
      .pipe(
        map((consultation: ApiConsultationCreateResponse) => {
          return prepareConsultation(consultation.data);
        })
      );
  }

  getConsultationsByDays(
    filter: Partial<GetConsultationsCountByDaysFilter>
  ): Observable<ConsultationCountByDays[]> {
    const params = getStringUrlParams(filter);

    return this.http
      .get<ApiResponse<ApiConsultationCountByDays[]>>(`${this.doctors4uaUrl}?${params}`)
      .pipe(
        map((result: ApiResponse<ApiConsultationCountByDays[]>) => {
          return result.data.map((consultation) => {
            return {
              id: consultation.id,
              ...consultation.attributes
            };
          });
        })
      );
  }

  getConsultationsList(filter: ConsultationsListFilter): Observable<DeserializedJsonInterface<Consultation[]>> {
    const params = getStringUrlParams(filter);

    return this.http
      .get(`${this.doctors4uaUrlV2}?${params}`)
      .pipe(
        map((result) => this.jsonaService.deserialize(result))
      );
  }

  getConsultationById(
    id: number,
    filter: GetConsultationByIdFilter
  ): Observable<DeserializedJsonInterface<Consultation>> {
    const params = getStringUrlParams(filter);

    return this.http
      .get(`${this.doctors4uaUrlV2}/${id}?${params}`)
      .pipe(
        map(result => this.jsonaService.deserialize(result))
      );
  }

  cancelConsultation(id: number): Observable<void> {
    return this.http.delete<void>(`${this.doctors4uaUrl}/${id}`);
  }

  changeConsultationStatus(id: number, statusId: number): Observable<void> {
    return this.http.get<void>(`${this.doctors4uaUrl}/change-status/${id}/${statusId}`);
  }

  createConsultationResult(filter: CreateConsultationResultFilter): Observable<void> {
    const params = getObjectUrlParams(filter);
    return this.http.post<void>(`${this.doctors4uaUrl}/result`, params);
  }

  addConsultationsDirections(id: number, directions: DirectionsInterface): Observable<any> {
    return this.http.patch(`${this.doctors4uaUrlV2}/${id}`, directions);
  }
}
